import { defineStore } from 'pinia';
import { computed, type ComputedRef, ref, watch } from 'vue';
import { type InstanceId, useInstanceIds } from '@/database/utils/alert.uses';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { snakeCase } from 'lodash-es';
import { useRepeat } from '@/worker/composables/useRepeat';
import { getAPIErrorStatusText } from '@/common/utils/commonUtils';
import { GroupItemInfo } from '@/common/components/organisms/alertListDetail/alertListDetail.types';
import { getV9AlertsAlertGroupCount } from '@/api-client/alert/queries/alert-common-v9-controller/alert-common-v9-controller';
import { type PlatformType } from '@/common/components/organisms/overviewAlert/overviewAlert.setup';

export interface CurrentAlertCount {
  criticalCount: number;
  warningCount: number;
}

/** 데이터베이스 알람 요청에 사용하는 플랫폼 타입 (인스턴스 + 클러스터 대상) */
export const DATABASE_ALERT_PLATFORM_TYPES: PlatformType[] = ['database', 'database_cluster'];

const useAlertCount = ({
  currentRouteInfo,
  instanceIds,
}: {
  currentRouteInfo: ComputedRef<{
    dbType: string;
    view: string;
  }>;
  instanceIds: ComputedRef<InstanceId[]>;
}) => {
  const isAlertLoading = ref(false);
  const errorStatusText = ref('');
  const groupList = ref<GroupItemInfo[]>([]);

  const frameName = computed(() => {
    const { dbType, view } = currentRouteInfo.value;
    return (
      FRAME_NAMES[`${dbType.toUpperCase()}_${snakeCase(view).toUpperCase()}`]?.ALERT_COUNT || ''
    );
  });

  const currentAlertCount = ref<CurrentAlertCount>({
    criticalCount: 0,
    warningCount: 0,
  });
  const getTotalCount = () => {
    const { criticalCount, warningCount } = currentAlertCount.value;
    return (criticalCount ?? 0) + (warningCount ?? 0);
  };
  const currentTotalCount = computed(() => getTotalCount());
  const totalBadgeCount = computed(() => {
    const total = getTotalCount();
    return total > 99 ? '99+' : `${total}`;
  });

  const initCount = () => {
    currentAlertCount.value = {
      criticalCount: 0,
      warningCount: 0,
    };
  };

  const fetchAlertGroupsCount = async ({
    instanceIdsFromRoute = [],
  }: {
    instanceIdsFromRoute?: string[];
  } = {}) => {
    const targetIds = instanceIdsFromRoute?.length ? instanceIdsFromRoute : instanceIds.value;
    if (!targetIds?.length) {
      initCount();
      return;
    }
    try {
      isAlertLoading.value = true;
      const {
        data: { data = [] },
      } = await getV9AlertsAlertGroupCount(
        { platformTypes: DATABASE_ALERT_PLATFORM_TYPES, targetIds },
        { params: { frameName: frameName.value, isPolling: true, isTimeout: false } },
      );
      groupList.value = data ?? [];

      currentAlertCount.value = groupList.value.reduce<CurrentAlertCount>(
        (acc, item) => ({
          criticalCount: acc.criticalCount + (item.criticalCount ?? 0),
          warningCount: acc.warningCount + (item.warningCount ?? 0),
        }),
        { criticalCount: 0, warningCount: 0 },
      );

      errorStatusText.value = '';
    } catch (e) {
      console.log(e);
      errorStatusText.value = getAPIErrorStatusText(e);
    } finally {
      isAlertLoading.value = false;
    }
  };

  const updatedFetchFlag = ref<number>();
  const fetchFn = () => {
    updatedFetchFlag.value = +new Date();
    fetchAlertGroupsCount();
  };

  const { resetFetch, clearFetch } = useRepeat(fetchFn, 5_000, {
    isSetup: false,
    isImmediate: false,
  });

  return {
    isAlertLoading,
    updatedFetchFlag,
    currentAlertCount,
    currentTotalCount,
    totalBadgeCount,
    errorStatusText,
    fetchAlertGroupsCount,
    resetFetch,
    clearFetch,
    initCount,
    groupList,
  };
};

export const useOverviewAlertStore = defineStore('overviewAlert', () => {
  const { currentRouteInfo, getInstanceIds } = useInstanceIds();
  const instanceIds = computed<InstanceId[]>(() => getInstanceIds());

  const {
    isAlertLoading,
    updatedFetchFlag,
    currentAlertCount,
    currentTotalCount,
    totalBadgeCount,
    errorStatusText,
    fetchAlertGroupsCount,
    resetFetch,
    clearFetch,
    initCount,
    groupList,
  } = useAlertCount({ currentRouteInfo, instanceIds });

  const isShowAlertDetail = ref(false);
  const openAlertSlide = () => {
    isShowAlertDetail.value = true;
  };

  watch(
    instanceIds,
    (curIds) => {
      if (!curIds?.length) {
        clearFetch();
        initCount();
        // 현재 ids 비었을때 그룹 id 날림
        groupList.value = [];
        return;
      }
      resetFetch();
    },
    { immediate: true },
  );

  return {
    isAlertLoading,
    updatedFetchFlag,
    currentAlertCount,
    currentTotalCount,
    totalBadgeCount,
    errorStatusText,
    fetchAlertGroupsCount,
    resetFetch,
    clearFetch,
    groupList,

    isShowAlertDetail,
    openAlertSlide,
  };
});
